import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { showSnackbar } from "../components/snackbar/Snackbar.slice";
import BrandingContext from "../context/BrandingContext";

const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_BYTES = 1024 * 1024;

const LEVEL_LABEL = {
  merchant: "merchant",
  iso: "ISO",
  acquirer: "acquirer",
};

const errorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
};

// Changes the logo of the user's own entity (merchant, ISO or acquirer).
// Users below inherit it unless they have their own.
const DashboardLogoDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { editableLevel, ownLogoUrl, uploadLogo, removeLogo } =
    useContext(BrandingContext);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleClose = () => {
    if (saving) return;
    setFile(null);
    onClose();
  };

  const handleFile = (event) => {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected) return;
    if (selected.size > MAX_BYTES) {
      dispatch(
        showSnackbar({ message: "The logo must be 1 MB or less.", severity: "warning" }),
      );
      return;
    }
    setFile(selected);
  };

  const run = async (action, successMessage, failMessage) => {
    setSaving(true);
    try {
      await action();
      dispatch(showSnackbar({ message: successMessage, severity: "success" }));
      setFile(null);
      onClose();
    } catch (error) {
      dispatch(
        showSnackbar({ message: errorMessage(error, failMessage), severity: "error" }),
      );
    } finally {
      setSaving(false);
    }
  };

  const level = LEVEL_LABEL[editableLevel] || "account";
  const shown = preview || ownLogoUrl;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Dashboard logo</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Logo shown in the side panel for your {level} and the users under it
          that don't have their own. PNG, JPG, WEBP or SVG, up to 1 MB.
        </DialogContentText>
        <Box
          sx={{
            height: 96,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            mb: 2,
          }}
        >
          {shown ? (
            <img
              src={shown}
              alt="Dashboard logo"
              style={{ maxWidth: "80%", maxHeight: 72, objectFit: "contain" }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              No logo of your own. The inherited or Blokko logo is shown.
            </Typography>
          )}
        </Box>
        <Button variant="outlined" component="label" disabled={saving}>
          Choose image
          <input hidden type="file" accept={ACCEPTED_TYPES} onChange={handleFile} />
        </Button>
        {file && (
          <Typography variant="body2" sx={{ mt: 1 }} noWrap>
            {file.name}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {ownLogoUrl && !file && (
          <Button
            color="error"
            disabled={saving}
            onClick={() =>
              run(removeLogo, "Logo removed.", "Could not remove the logo. Try again.")
            }
            sx={{ mr: "auto" }}
          >
            Remove logo
          </Button>
        )}
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!file || saving}
          onClick={() =>
            run(() => uploadLogo(file), "Logo updated.", "Could not save the logo. Try again.")
          }
        >
          {saving ? "SAVING..." : "SAVE"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardLogoDialog;
